const axios = require('axios');
const { ethers } = require('ethers');
const { gql, request } = require('graphql-request');
const BeaconChainService = require('./services/beacon-chain.service');
const { _getChainSpecificConstants, _extractChainID, customErrors } = require('./constants.js');
const { getContractInstance } = require('./contracts');
const _ = require('lodash');

const _add0x = (data) => {

	if(!data) {
		throw customErrors.NULL_OR_UNDEFINED_VALUE;
	}

	if(Buffer.isBuffer(data)) {
		return data;
	}

	if(data instanceof Uint8Array) {
		return '0x' + Buffer.from(data).toString('hex');
	}

	if(_.isString(data)) {
		return (data.indexOf('0x') !== -1) ? data : '0x'.concat(data);		
	}

	return data;
};

const _remove0x = (data) => {

	if(!data) {
		throw customErrors.NULL_OR_UNDEFINED_VALUE;
	}

	if(Buffer.isBuffer(data)) {
		return data;
	}

	if(data instanceof Uint8Array) {
		return Buffer.from(data).toString('hex');
	}

	if(_.isString(data)) {
		return (data.indexOf('0x') !== -1) ? data.slice(2) : data;
	}

	return data;
};

const _getLastDepositIndex = async (signer, blsPubKey) => {

	const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

	const lookupQuery = gql`
		query getLastDepositIndex($id: String!) {
			stakehouseAccount(id: $id) {
				lastDepositDepositCount
			}
		}
	`;
  
	const response = await request(
		stakehouseUrls.SUBGRAPH_ENDPOINTS,
		lookupQuery,
		{
			id: _add0x(blsPubKey.toLowerCase())
		}
	);
  
	if (!response || !response.stakehouseAccount || !response.stakehouseAccount.lastDepositDepositCount) {
		throw new Error('Invalid response getting lastDepositDepositCount')
	}

	return _.get(response, 'stakehouseAccount.lastDepositDepositCount');
};

const _getFinalisedEpochReport = async (signer, beaconNodeURL, blsPublicKey) => {

    const chainID = await _extractChainID(signer);
    const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const beaconChainService = new BeaconChainService(beaconNodeURL, stakehouseUrls.SUBGRAPH_ENDPOINTS);
    blsPublicKey = _remove0x(blsPublicKey);

    let finalisedBeaconChainReport = await beaconChainService.getFinalisedEpochReport(
        blsPublicKey
    );

    finalisedBeaconChainReport.lastDepositIndex = await _getLastDepositIndex(signer, blsPublicKey);

    return finalisedBeaconChainReport;
};

const _checkForEligibility = async (signer, finalisedBeaconChainReport) => {

    const blsPublicKey = _add0x(finalisedBeaconChainReport.blsPublicKey);
    const currentCheckpointEpoch = finalisedBeaconChainReport.currentCheckpointEpoch;

    const contract = (await getContractInstance(signer)).accountManager();

    const lastKnownCheckpointEpoch = (await contract.blsPublicKeyToLastState(_add0x(blsPublicKey))).currentCheckpointEpoch;

    return lastKnownCheckpointEpoch.lt(currentCheckpointEpoch);
};

const _authenticateReport = async (signer, beaconNodeURL, finalisedBeaconChainReport) => {

    const isEligibile = await _checkForEligibility(signer, finalisedBeaconChainReport);
    if(!isEligibile) {
        throw new Error(customErrors.PAST_EPOCH);
    }

    const chainID = await _extractChainID(signer);
    const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    try {
        const client = axios.create({
            baseURL: beaconNodeURL,
            headers: {
              'Accept': 'application/json'
            }
        });
        const res = await client.get('/eth/v2/beacon/blocks/finalized');
        const beaconChainDepositCount = ethers.BigNumber.from(res.data.data.message.body.eth1_data.deposit_count);
        let subgraphDepositCount = await _getLastDepositIndex(signer, finalisedBeaconChainReport.blsPublicKey);
        subgraphDepositCount = ethers.BigNumber.from(subgraphDepositCount);
        
        if(subgraphDepositCount.gt(beaconChainDepositCount)) {
            throw `Error: Unprocessed deposits detected. deposit ID: ${subgraphDepositCount} Beacon chain processed deposit ID: ${beaconChainDepositCount}`;
        }

        const routerResponse = await axios.post(stakehouseUrls.BEACON_AUTH_ENDPOINT, finalisedBeaconChainReport);
        return routerResponse.data;
    } catch(e) {
        return e;
    }
};

module.exports = {
    _getFinalisedEpochReport,
    _authenticateReport
};
